import { useCallback, useEffect, useState } from 'react';
import { useAdminDispatch } from 'admin/reducers';
import {
	getIntegrationToken,
	getUserIntegrations,
	postGoogleOAuth,
	UserIntegrationItem,
} from 'admin/actions/integrations';

const isIntegrationReady = (integration: UserIntegrationItem | null): integration is UserIntegrationItem => {
	return !!integration && integration.status === 'ready';
};

type GoogleAuthPayload = { code: string; state: string } | null;

export const useGoogleUserIntegrations = (authData: GoogleAuthPayload) => {
	const dispatch = useAdminDispatch();
	const [accessToken, setAccessToken] = useState<Nullable<string>>(null);
	const [userIntegrations, setUserIntegrations] = useState<UserIntegrationItem[]>([]);
	const [userIntegration, setUserIntegrationState] = useState<Nullable<UserIntegrationItem>>(null);
	const [loading, setLoading] = useState<Nullable<string>>(null);

	const setUserIntegration = useCallback(
		async (nextIntegration: UserIntegrationItem | null = null) => {
			setAccessToken(null);
			setUserIntegrationState(nextIntegration);

			if (isIntegrationReady(nextIntegration)) {
				const response = await dispatch(getIntegrationToken({ id: nextIntegration.id }));
				if (response.success) {
					setAccessToken(response.result?.accessToken ?? null);
				}
			}
		},
		[dispatch]
	);

	const refetch = useCallback(async () => {
		setLoading('init');
		const userIntegrationsResponse = await dispatch(getUserIntegrations());
		const list = userIntegrationsResponse.result ?? [];
		setUserIntegrations(list);
		if (list.length) {
			await setUserIntegration(list.find(o => o.provider === 'google') ?? null);
		} else {
			await setUserIntegration(null);
		}
		setLoading(null);
	}, [dispatch, setUserIntegration]);

	useEffect(() => {
		refetch();
	}, [refetch, authData]);

	const signIn = useCallback(
		async (id?: string) => {
			const oauth = await dispatch(postGoogleOAuth({ id }));
			if (oauth.result?.url) {
				window.open(oauth.result.url, 'popupWindow', 'width=500,height=600');
			}
		},
		[dispatch]
	);

	return {
		userIntegrations,
		userIntegration,
		accessToken,
		loading,
		setUserIntegration,
		signIn,
		refetch,
	};
};
